import { useState, useEffect, useCallback, useMemo } from "react";
import { getSupabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { LOG_ACTIONS } from "@/hooks/useDealerLog";

interface DealerLog {
  id: string;
  dealer_user_id: string | null;
  action: string;
  details: string | null;
  ip_address: string | null;
  created_at: string;
  user_name?: string;
}

interface DealerUser {
  id: string;
  full_name: string;
}

const actionIcons: Record<string, string> = {
  [LOG_ACTIONS.LOGIN]: "ri-login-box-line",
  [LOG_ACTIONS.LOGOUT]: "ri-logout-box-line",
  [LOG_ACTIONS.PRODUCT_ADD]: "ri-add-box-line",
  [LOG_ACTIONS.PRODUCT_UPDATE]: "ri-edit-box-line",
  [LOG_ACTIONS.PRODUCT_DELETE]: "ri-delete-bin-line",
  [LOG_ACTIONS.CATEGORY_ADD]: "ri-price-tag-3-line",
  [LOG_ACTIONS.CATEGORY_UPDATE]: "ri-price-tag-3-line",
  [LOG_ACTIONS.CATEGORY_DELETE]: "ri-price-tag-3-line",
  [LOG_ACTIONS.SALE_CREATE]: "ri-shopping-cart-2-line",
  [LOG_ACTIONS.SALE_UPDATE]: "ri-edit-line",
  [LOG_ACTIONS.SALE_DELETE]: "ri-delete-bin-line",
  [LOG_ACTIONS.PAYMENT_COLLECT]: "ri-money-dollar-circle-line",
  [LOG_ACTIONS.STAFF_ADD]: "ri-user-add-line",
  [LOG_ACTIONS.STAFF_UPDATE]: "ri-user-settings-line",
  [LOG_ACTIONS.STAFF_DELETE]: "ri-user-unfollow-line",
  [LOG_ACTIONS.BRANCH_ADD]: "ri-store-2-line",
  [LOG_ACTIONS.BRANCH_UPDATE]: "ri-store-2-line",
  [LOG_ACTIONS.BRANCH_DELETE]: "ri-store-2-line",
  [LOG_ACTIONS.SUPPLIER_ADD]: "ri-truck-line",
  [LOG_ACTIONS.SUPPLIER_UPDATE]: "ri-truck-line",
  [LOG_ACTIONS.SUPPLIER_DELETE]: "ri-truck-line",
  [LOG_ACTIONS.STOCK_UPDATE]: "ri-archive-line",
  [LOG_ACTIONS.IMPORT]: "ri-upload-cloud-2-line",
  [LOG_ACTIONS.SETTINGS_UPDATE]: "ri-settings-3-line",
  [LOG_ACTIONS.CURRENCY_UPDATE]: "ri-money-dollar-circle-line",
  [LOG_ACTIONS.BANK_ACCOUNT_ADD]: "ri-bank-line",
  [LOG_ACTIONS.BANK_ACCOUNT_UPDATE]: "ri-bank-line",
  [LOG_ACTIONS.BANK_ACCOUNT_DELETE]: "ri-bank-line",
  [LOG_ACTIONS.USER_ADD]: "ri-user-add-line",
  [LOG_ACTIONS.USER_UPDATE]: "ri-user-settings-line",
  [LOG_ACTIONS.USER_DELETE]: "ri-user-unfollow-line",
  [LOG_ACTIONS.BACKUP]: "ri-database-2-line",
  [LOG_ACTIONS.PAYMENT_NOTIFICATION]: "ri-notification-3-line",
};

const actionColors: Record<string, string> = {
  giris: "bg-primary-100 text-primary-700",
  cikis: "bg-foreground-100 text-foreground-600",
  urun: "bg-accent-100 text-accent-700",
  kategori: "bg-secondary-100 text-secondary-700",
  satis: "bg-emerald-100 text-emerald-700",
  tahsilat: "bg-emerald-100 text-emerald-700",
  personel: "bg-amber-100 text-amber-700",
  sube: "bg-cyan-100 text-cyan-700",
  tedarikci: "bg-violet-100 text-violet-700",
  stok: "bg-rose-100 text-rose-700",
  ithalat: "bg-indigo-100 text-indigo-700",
  ayar: "bg-foreground-100 text-foreground-600",
  banka: "bg-teal-100 text-teal-700",
  kullanici: "bg-orange-100 text-orange-700",
  yedekleme: "bg-slate-100 text-slate-700",
  odeme: "bg-lime-100 text-lime-700",
};

function getActionColor(action: string): string {
  const lower = action.toLowerCase();
  if (lower.includes("giriş")) return actionColors.giris;
  if (lower.includes("çıkış")) return actionColors.cikis;
  if (lower.includes("ürün")) return actionColors.urun;
  if (lower.includes("kategori")) return actionColors.kategori;
  if (lower.includes("satış")) return actionColors.satis;
  if (lower.includes("tahsilat")) return actionColors.tahsilat;
  if (lower.includes("personel")) return actionColors.personel;
  if (lower.includes("şube")) return actionColors.sube;
  if (lower.includes("tedarikçi")) return actionColors.tedarikci;
  if (lower.includes("stok")) return actionColors.stok;
  if (lower.includes("i̇çe aktar") || lower.includes("i̇thal")) return actionColors.ithalat;
  if (lower.includes("ayar")) return actionColors.ayar;
  if (lower.includes("banka")) return actionColors.banka;
  if (lower.includes("kullanıcı")) return actionColors.kullanici;
  if (lower.includes("yedekleme")) return actionColors.yedekleme;
  if (lower.includes("ödeme") || lower.includes("bildirim")) return actionColors.odeme;
  return "bg-foreground-100 text-foreground-600";
}

function getActionIcon(action: string): string {
  const lower = action.toLowerCase();
  if (lower.includes("giriş")) return "ri-login-box-line";
  if (lower.includes("çıkış")) return "ri-logout-box-line";
  if (lower.includes("ürün ekleme")) return "ri-add-box-line";
  if (lower.includes("ürün güncelleme")) return "ri-edit-box-line";
  if (lower.includes("ürün silme")) return "ri-delete-bin-line";
  if (lower.includes("kategori")) return "ri-price-tag-3-line";
  if (lower.includes("satış")) return "ri-shopping-cart-2-line";
  if (lower.includes("tahsilat")) return "ri-money-dollar-circle-line";
  if (lower.includes("personel")) return "ri-team-line";
  if (lower.includes("şube")) return "ri-store-2-line";
  if (lower.includes("tedarikçi")) return "ri-truck-line";
  if (lower.includes("stok")) return "ri-archive-line";
  if (lower.includes("i̇çe aktar")) return "ri-upload-cloud-2-line";
  if (lower.includes("ayar")) return "ri-settings-3-line";
  if (lower.includes("banka")) return "ri-bank-line";
  if (lower.includes("kullanıcı")) return "ri-user-settings-line";
  if (lower.includes("yedekleme")) return "ri-database-2-line";
  if (lower.includes("ödeme") || lower.includes("bildirim")) return "ri-notification-3-line";
  return "ri-file-list-3-line";
}

const uniqueActions = Object.values(LOG_ACTIONS);

export default function DealerAdminLogsPage() {
  const { user } = useAuth();
  const dealerId = user?.dealerId;

  const [logs, setLogs] = useState<DealerLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState<string>("Tümü");
  const [expandedLog, setExpandedLog] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!dealerId) return;
    setLoading(true);
    setError("");
    try {
      const supabase = getSupabase();

      const { data: logData, error: logErr } = await supabase
        .from("dealer_logs")
        .select("id, dealer_user_id, action, details, ip_address, created_at")
        .eq("dealer_id", dealerId)
        .order("created_at", { ascending: false })
        .limit(500);

      if (logErr) throw logErr;

      const { data: userData, error: userErr } = await supabase
        .from("dealer_users")
        .select("id, full_name")
        .eq("dealer_id", dealerId);

      const userMap: Record<string, string> = {};
      if (!userErr && userData) {
        (userData as DealerUser[]).forEach((u) => {
          userMap[u.id] = u.full_name;
        });
      }

      const enriched = (logData || []).map((log: any) => ({
        ...log,
        user_name: log.dealer_user_id ? userMap[log.dealer_user_id] || "Bilinmeyen" : "Sistem",
      }));

      setLogs(enriched);
    } catch (e: any) {
      setError(e.message || "Loglar yüklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = useMemo(() => {
    return logs.filter((log) => {
      if (actionFilter !== "Tümü" && log.action !== actionFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        log.action.toLowerCase().includes(q) ||
        (log.details || "").toLowerCase().includes(q) ||
        (log.user_name || "").toLowerCase().includes(q)
      );
    });
  }, [logs, search, actionFilter]);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

  const logCounts = useMemo(() => {
    const counts: Record<string, number> = { "Tümü": logs.length };
    uniqueActions.forEach((action) => {
      counts[action] = logs.filter((l) => l.action === action).length;
    });
    return counts;
  }, [logs]);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center">
              <i className="ri-file-list-3-line text-secondary-600"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950">İşlem Logları</h1>
          </div>
          <p className="text-sm text-foreground-500 mt-0.5">
            Sistemde yapılan tüm işlemlerin detaylı kaydı · <strong className="text-foreground-700">{logs.length}</strong> kayıt
          </p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
          Yenile
        </button>
      </div>

      {/* Filters */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Log ara... (işlem, detay, kullanıcı)"
              className="w-full pl-9 pr-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-secondary-400"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          {["Tümü", ...uniqueActions].map((action) => {
            const count = logCounts[action] || 0;
            if (action !== "Tümü" && count === 0) return null;
            const isActive = actionFilter === action;
            return (
              <button
                key={action}
                onClick={() => setActionFilter(action)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  isActive
                    ? "bg-secondary-500 text-background-50"
                    : "bg-background-100 text-foreground-500 hover:bg-background-200/70"
                }`}
              >
                {action}
                <span className={`ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold ${
                  isActive ? "bg-background-50/20 text-background-50" : "bg-background-200/70 text-foreground-400"
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl px-4 py-3 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
          <button onClick={fetchData} className="ml-auto text-red-600 font-medium cursor-pointer whitespace-nowrap">Tekrar Dene</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-background-50 border border-background-200/70 rounded-xl py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-file-list-3-line text-2xl text-foreground-300"></i>
          </div>
          <p className="text-foreground-500 font-medium">Log kaydı bulunamadı</p>
          <p className="text-xs text-foreground-400 mt-1">
            {logs.length === 0
              ? "Henüz hiçbir işlem loglanmamış. Sistem kullanıldıkça loglar burada görünecek."
              : "Filtrelere uygun log kaydı bulunamadı."}
          </p>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead>
                <tr className="bg-background-100 text-left">
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500 w-44">Tarih / Saat</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">İşlem</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Kullanıcı</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Detay</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((log) => (
                  <tr
                    key={log.id}
                    className="border-t border-background-200/70 hover:bg-background-100/50 transition-colors cursor-pointer"
                    onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                  >
                    <td className="px-4 py-3 text-foreground-500 text-xs whitespace-nowrap font-mono">
                      {formatDate(log.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                        <i className={`${getActionIcon(log.action)} text-xs`}></i>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-xs text-foreground-700">
                        <i className="ri-user-line text-foreground-400"></i>
                        {log.user_name || "Sistem"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-foreground-600 max-w-md">
                        {expandedLog === log.id ? (
                          <span>{log.details || "-"}</span>
                        ) : (
                          <span className="line-clamp-1">{log.details || "-"}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Info Note */}
      <div className="bg-amber-50 border border-amber-200/70 rounded-xl p-3 flex items-start gap-2">
        <i className="ri-information-line text-amber-500 mt-0.5"></i>
        <div>
          <p className="text-xs font-medium text-amber-700">Log kayıtları silinemez</p>
          <p className="text-xs text-amber-600 mt-0.5">
            Tüm işlem logları kalıcı olarak saklanır. Güvenlik ve denetim amacıyla log kayıtları üzerinde silme veya düzenleme işlemi yapılamaz.
          </p>
        </div>
      </div>
    </div>
  );
}